'use client';

import { CityPageQueryQuery } from '@medprev-web-app/api-graphql-client';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import Image from 'next/image';

import { SearchSectionCtaBoxButtons } from './common/search-section-cta-box-buttons.component';
import { SearchSectionCtaButtons } from './common/search-section-cta-buttons.component';
import { SearchSectionSubtitle } from './titles/search-section-subtitle.component';
import { SearchSectionTitle } from './titles/search-section-title.component';

import onePersonMobile from '@/app/(main)/(pages)/_components/search-section/assets/search-section-one-person-mobile.png';
import onePerson from '@/app/(main)/(pages)/_components/search-section/assets/search-section-one-person.png';
import persons from '@/app/(main)/(pages)/_components/search-section/assets/search-section-persons.png';
import { getServiceChannelFlags } from '@/app/(main)/(pages)/_utils/business-model.util';
import { ContentContainer } from '@/components/ui/content-container.component';
import { cn } from '@/utils/cn-tw-merge';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface SearchSectionCityProps {
  title: string;
  cityConfig: CityPageQueryQuery;
}

export const SearchSectionCity = ({ title, cityConfig }: SearchSectionCityProps) => {
  const {
    hasOneCta,
    noServiceType,
    onlyTelemedicineServiceChannel,
  } = getServiceChannelFlags(cityConfig);

  return (
    <section
      data-testid={DataTestIdEnum.CITY_SEARCH_SECTION_CITY}
      className="flex bg-green-500 w-full pt-10 h-[436px] sm:h-auto"
    >
      <ContentContainer
        className={cn(
          'flex flex-col xl:flex-row justify-between items-center xl:gap-10 relative',
          hasOneCta ? '' : 'xl:items-end',
        )}
      >
        <div className={cn('w-full 3xl:w-1/3', hasOneCta ? '' : 'xl:mb-32')}>
          <SearchSectionTitle className="sm:static sm:mb-2">
            {title}
          </SearchSectionTitle>

          <SearchSectionSubtitle
            noServiceType={noServiceType}
            hasTelemedicine={onlyTelemedicineServiceChannel}
            className="hidden md:block mr-0 md:mr-40 lg:mr-30 xl:mr-32 3xl:mr-0 mb-4"
          />

          <SearchSectionSubtitle
            noServiceType={noServiceType}
            hasTelemedicine={onlyTelemedicineServiceChannel}
            className="mr-24 pre-mobile:mr-40 sm:hidden"
          />

          {hasOneCta && (
            <div className="hidden xl:flex">
              <SearchSectionCtaButtons
                cityConfig={cityConfig}
                medprevScheduleButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BY_WEBSITE}
                assistedPurchaseButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_SUPPORT}
                telemedicineButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_TELEHEALTH}
                from="hero"
                sessionName="chamada_principal"
              />
            </div>
          )}
        </div>

        <Image
          src={persons}
          alt="Colaboradores"
          height={370}
          draggable={false}
          priority
          className="hidden lg:block"
        />

        <div className="hidden md:flex lg:hidden w-full justify-end">
          <Image
            src={persons}
            alt="Colaboradores"
            height={360}
            draggable={false}
            priority
            className="-mx-24 object-contain"
          />
        </div>

        <div className="hidden sm:flex md:hidden w-full">
          <SearchSectionSubtitle
            noServiceType={noServiceType}
            hasTelemedicine={onlyTelemedicineServiceChannel}
            className="sm:mr-20 object-contain"
          />

          <Image
            src={onePerson}
            alt="Colaborador"
            height={340}
            draggable={false}
            priority
            className="-mx-11 object-contain"
          />
        </div>

        <div className="flex flex-col sm:hidden w-full">
          <div className="absolute bottom-0 right-0 flex items-end justify-end w-full">
            <Image
              src={onePersonMobile}
              alt="Colaborador"
              height={330}
              draggable={false}
              priority
              className="object-contain"
            />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 w-full">
          <SearchSectionCtaBoxButtons hasOneCta={hasOneCta}>
            <SearchSectionCtaButtons
              cityConfig={cityConfig}
              medprevScheduleButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_BY_WEBSITE}
              assistedPurchaseButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_SCHEDULE_SUPPORT}
              telemedicineButtonAnalyticsEventName={AnalyticsEnum.CLICK_CITY_BUTTON_TELEHEALTH}
              from="hero"
              sessionName="chamada_principal"
            />
          </SearchSectionCtaBoxButtons>
        </div>
      </ContentContainer>
    </section>
  );
};
